
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { UserType } from '@/types/user';
import { useSearchParams } from 'react-router-dom';

interface SignUpResult {
  success: boolean;
  error: string | null;
  user?: any;
  confirmEmail?: boolean;
}

export const useSignUp = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signupComplete, setSignupComplete] = useState(false);
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const signUp = async (email: string, password: string): Promise<SignUpResult> => {
    setError(null);
    setLoading(true);
    
    try {
      // Check rate limit first
      const { data: ipData, error: ipError } = await supabase.functions.invoke('check-rate-limit', {
        body: { action: 'signup' }
      });
      
      if (ipError || (ipData && ipData.limited)) {
        throw new Error(ipData?.message || 'Too many signup attempts. Please try again later.');
      }
      
      console.log('Creating account for:', email);
      
      // Create Stripe customer
      const { data: stripeData, error: stripeError } = await supabase.functions.invoke('create-stripe-customer', {
        body: { email }
      });
      
      if (stripeError) {
        console.error('Error creating Stripe customer:', stripeError);
      }
      
      const stripeCustomerId = stripeData?.customerId;
      
      // Update user_type to 'standard' to match what's allowed in the database
      // IMPORTANT: Use only valid user_type values that match the database constraint
      // Valid values are: 'admin', 'free', 'starter', 'pro', 'premium', 'standard'
      const userType: UserType = 'standard';
      
      // Sign up the user with metadata
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            user_type: userType,
            used_free_report: false,
            stripe_customer_id: stripeCustomerId || null
          },
          emailRedirectTo: `${window.location.origin}/auth`
        }
      });

      if (error) {
        console.error('Signup error details:', error);
        throw error;
      }

      console.log('Signup successful, user data:', data);
      setSignupComplete(true);
      
      // Check if email confirmation is required
      const isEmailConfirmationRequired = !data.session;
      
      if (!isEmailConfirmationRequired) {
        // User was automatically signed in
        toast({
          title: "Account created",
          description: "Your account has been created successfully with the standard plan.",
        });
        return { success: true, error: null, user: data.user, confirmEmail: false };
      } else {
        // Email confirmation required
        toast({
          title: "Account created",
          description: "Please check your email to confirm your account before signing in.",
        });
        return { success: true, error: null, user: data.user, confirmEmail: true };
      }
      
    } catch (err: any) {
      console.error('Error signing up:', err.message);
      
      let userMessage = 'An unexpected error occurred during sign up';
      
      if (err.message.includes('violates check constraint')) {
        userMessage = 'There was an issue with your account type. Please contact support.';
      } else if (err.message.includes('duplicate key')) {
        userMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (err.message) {
        userMessage = err.message;
      }
      
      setError(userMessage);
      return { success: false, error: userMessage };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    signUp,
    signupComplete,
    setError
  };
};
